<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
    ref="gc-base-layout"
    class="goods-in"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Goods In'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode != 'review'
            ? 'Create Goods In for PO # ' + baseView.id
            : 'Goods In ' + baseView.id
        "
        :secondaryLabel="
          baseView.mode == 'review' || baseView.mode == 'edit'
            ? dataModel.SupplierName
            : undefined
        "
        v-if="loaded"
      />
    </template>

    <div v-if="loaded" class="css-v2 transaction-screen">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="loaded ? dataModel : {}"
          :itemSaveCallBack="saveTransaction"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-data-grid-so-v5 data-grid-details">
        <dx-tooltip :ref="tooltipRefName" position="top">
          <div class="tooltipContent">
            <div>Select to Include Lines in Goods In</div>
          </div>
        </dx-tooltip>

        <dx-tooltip :ref="CompleteTooltipRefName" position="top">
          <div class="tooltipContent">
            <div>Select to Mark Lines Complete</div>
          </div>
        </dx-tooltip>

        <dx-tooltip :ref="ProdBatchExpRefName" position="top">
          <div class="tooltipContent">
            <div>
              The Entered Expiry Date is earlier than the Expected Minimum
              Expiry Date for this product.
            </div>
          </div>
        </dx-tooltip>
        <dx-data-grid
          :columns="getColumns"
          :data-source="getLocalDataSource"
          :height="getGridHeight"
          :column-chooser="{ enabled: false }"
          :repaint-changes-only="true"
          :show-borders="true"
          :two-way-binding-enabled="false"
          :focusedRowEnabled="true"
          :ref="'goods-in-grid'"
          :selectedRowKeys.sync="selectedRowKeys"
          @cell-prepared="onCellPrepared"
          @selection-changed="onSelectionChanged"
          @toolbar-preparing="onToolbarPreparing($event)"
          @editor-preparing="onEditorPreparing"
          @content-ready="onContentReady"
          @editing-start="onEditingStart"
          @focused-cell-changed="onFocusedCellChanged"
        >
          <DxSelection mode="multiple" showCheckBoxesMode="always" />
          <DxEditing
            :allow-deleting="false"
            :allow-updating="baseView.mode != 'review'"
            :mode="'cell'"
          />

          <DxKeyboardNavigation
            :edit-on-key-press="true"
            :enter-key-action="'startEdit'"
            :enter-key-direction="'none'"
            :enabled="true"
          />

          <DxPaging :page-size="200" :enabled="false" />

          <DxPager
            :showInfo="true"
            :showPageSizeSelector="false"
            :infoText="'({2} items)'"
            :visible="false"
            class="gc-pager"
            cssClass="gc-pages-2"
          />
          <template #Record="{ data }">
            <GcRecord
              :data="data"
              :primary="data.column['cellTemplateRecord']['primary']"
              :secondary="data.column['cellTemplateRecord']['secondary']"
            />
          </template>

          <template #Decimal="{ data }">
            <GcDecimal :dataValue="data" :format="data.column.format" />
          </template>

          <template #complete-header-template>
            <div>
              <DxCheckBox
                :ref="'complete-header-template'"
                :on-value-changed="onCompleteHeaderUpdate"
                :disabled="getDisableCompleteHeader"
              />
            </div>
          </template>

          <template #complete-template="{ data: cellInfo }">
            <div>
              <DxCheckBox
                :value="cellInfo.data.Completed"
                :on-value-changed="
                  (e) => {
                    if (cellInfo.setValue) cellInfo.setValue(e.value);
                  }
                "
                :disabled="getLineCompleteStatus(cellInfo.data)"
              />
            </div>
          </template>

          <template #menu-cell-template="{ data }">
            <GcMenuCellTemplate
              class="invoice-print-btn"
              :data="data"
              :menuOptions="data.column.ellipsisMenu"
              :onItemClickCallBack="onItemClickCallBack"
              :visible="data.column.visible"
              :disabled="
                data.column.makeDisabled
                  ? data.column.makeDisabled(data)
                  : false
              "
            />
          </template>

          <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
          <template #masterDetailTemplate="{ data: lineItem }">
            <dx-data-grid
              :data-source="lineItem.data.ChildDataSource"
              :columns="getChildGridColumns(lineItem)"
              :columnChooser="{
                enabled: false,
              }"
              :searchPanel="{
                visible: false,
              }"
              :pager="{ visible: false }"
              :ref="'master-details-' + lineItem.data.LineNo"
              :elementAttr="{ class: 'internal-grid' }"
              @editing-start="onBatchEditingStart"
              @row-removed="onBatchRowRemoved"
              @editor-preparing="onBatchEditorPreparing"
              @cell-prepared="onBatchCellPrepared"
              @focused-cell-changed="onBatchFocusedCellChanged"
              :focusedRowEnabled="true"
              :key-expr="'LineNo'"
              @row-prepared="onBatchRowPrepared"
            >
              <DxEditing
                :allow-updating="true"
                :allow-deleting="true"
                mode="cell"
              />

              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>

              <template #exp-date-template="{ data }">
                <div class="exp-date-template">
                  <span>{{ data.text }}</span>
                  <div v-if="data.data.isValid === false">
                    <i
                      class="dx-icon dx-icon-info"
                      @mouseover="handleIconHover($event)"
                      @mouseout="handleIconLeave($event)"
                    ></i>
                  </div>
                </div>
              </template>
            </dx-data-grid>
          </template>
        </dx-data-grid>

        <GcFooter v-if="showFooter && loaded">
          <template #leftContent>
            <div
              class="gc-grid-footer-item left-content"
              v-for="item in footerLeftItems"
              :key="item.label"
            >
              <label class="gc-footer-label">{{ item.label }}</label>

              <label
                v-if="item.fieldType == 'custome-string'"
                class="gc-footer-value"
                >{{ item.value }}</label
              >
              <GcFooterItem
                :value="item.value"
                :format="item.format"
                :length="item.format == 'Decimal-16-4C' ? 8 : 4"
                v-else
              />
            </div>
          </template>
          <template #rightContent>
            <div
              class="gc-grid-footer-item"
              v-for="item in footerRightItems"
              :key="item.label"
            >
              <label class="gc-footer-label">{{ item.label }}</label>
              <GcFooterItem
                :value="item.value"
                :format="item.format"
                :length="item.format == 'Decimal-16-4C' ? 8 : 6"
              />
            </div>
          </template>
        </GcFooter>
      </div>
    </div>

    <!-- Add New Line PopUp -->
    <GcPopUpAddSalesOrderItem
      v-if="addGoodsInItem && addGoodsInItem.visible"
      :setVisible="addGoodsInItem.visible"
      :callBack="addGoodsInItem.popUpCallBack"
      :addSalesOrderItemData="addGoodsInItem.addSalesOrderItemData"
      :formContentReady="addGoodsInItem.formContentReady"
      :baseView="baseView"
      :setLoading="showLoading"
    ></GcPopUpAddSalesOrderItem>

    <!-- Edit Line PopUp -->
    <GcPopUpEditProductItem
      v-if="editGoodsInItem && editGoodsInItem.visible"
      :setVisible="editGoodsInItem.visible"
      :callBack="editGoodsInItem.popUpCallBack"
      :editProductItemData="editGoodsInItem.editProductItemData"
      :formContentReady="editGoodsInItem.formContentReady"
      :baseView="baseView"
      :setLoading="showLoading"
    ></GcPopUpEditProductItem>
    <GcPopupLabelPrinting
      v-if="labelPrintingVisible"
      :visible.sync="labelPrintingVisible"
      :transaction-no="labelPrintingTransactionNo"
      :purchase-order-no="purchaseOrderDetail.PurchaseOrderCode"
      @update:visible="onLabelPrintingClose"
    />
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { GoodsInListService } from "../../_services/api/purchasing/goods-in-list.service";
import { GoodsInService } from "../../_services/api/purchasing/goods-in.service";
import { SupplierService } from "../../_services/api/suppliers/supplier.service";
import validationEngine from "devextreme/ui/validation_engine";
import { mapGetters } from "vuex";
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import DxTooltip from "devextreme-vue/tooltip";
import { on } from "devextreme/events";
import CheckBox from "devextreme/ui/check_box";
import { formatNumber } from "devextreme/localization";
import { getFactorByCode, getGlobalField } from "../../_store/helpers";
import { FeatureStorage } from "../../_services/storage";
import { DxMasterDetail } from "devextreme-vue/data-grid";
import GcPopupLabelPrinting from "../../popups/purchasing/gc-popup-label-printing.vue";

import DataGridHelpers from "@/_helpers/data-grid/helpers";
import SettingsService from "../../_services/api/settings/graycorp/settings.service";
const { countLineBreaks } = DataGridHelpers;

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: { DxTooltip, DxMasterDetail, GcPopupLabelPrinting },
  data() {
    return {
      tooltipRefName: "tooltip",
      CompleteTooltipRefName: "completeTooltip",
      ProdBatchExpRefName: "prodBatchExpTooltip",
      CheckStockWriteOff: false,
      loaded: false,
      setLoading: false,
      headerEndpoint: undefined,
      dataModel: {
        DateIn: new Date(),
        DateReceived: new Date(),
        SupplierCurrencyCode: "FX",
      },
      reload: false,
      validationSummary: [],
      ui: {
        DateInMin: null,
        DateInMax: null,
      },
      purchaseOrderDetail: {},
      serverError: {},
      refreshDataGrid: false,
      refreshColumns: undefined,
      addGoodsInItem: undefined,
      editGoodsInItem: undefined,
      expandChildGrid: false,
      gridDataLoaded: false,
      CollapseBtnContentReady: false,
      BatchGridInstance: undefined,
      headerToolBarForm: undefined,
      IsBatchAvailable: false,
      tabInToComment: false,
      localDataSource: new ArrayStore({
        key: "LineNo",
        data: [],
      }),
      selectedRowKeys: [],
      showFooter: true,
      footerItems: [],
      batchNoTabKey: false,
      goodsInValidateShelfLife: false,
      goodsInLabelPrintEnabled: false,
      labelPrintingVisible: false,
      labelPrintingTransactionNo: null,
    };
  },
  async created() {
    this.showLoading(true);
    this.initialized();
  },
  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    backButtonRoute() {
      return "/goods-in";
    },
    getHeaderToolbar() {
      return {
        toolbarFormItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            items: [
              {
                dataField: "DateIn",
                label: {
                  text: "Date",
                },
                editorType: "dxDateBox",
                editorOptions: {
                  displayFormat: "dd MMM yyyy",
                  width: 120,
                  min: this.ui.DateInMin,
                  max: this.ui.DateInMax,
                  onFocusIn: (e) => {
                    e.event.target.select();
                  },
                },
                validationRules: [
                  {
                    type: "required",
                    message: "Date is required",
                  },
                ],
              },
              {
                dataField: "DateReceived",
                label: "Date Received",
                editorType: "dxDateBox",
                editorOptions: {
                  displayFormat: "dd MMM yyyy",
                  width: 120,
                  onKeyDown: (e) => {
                    this.onDateReceivedKeyDown(e);
                  },
                },
                validationRules: [
                  {
                    type: "required",
                    message: "Date Received is required",
                  },
                ],
              },
            ],
          },
        ],
        save: true,
        // saveCallback: this.saveGoodsIn,
        formContentReady: this.formContentReady,
      };
    },
    getColumns() {
      return this.parseColumnWithScreenFields([
        {
          caption: "",
          dataField: "",
          type: "detailExpand",
          fixed: true,
          fixedPosition: "left",
          visibleIndex: 0,
          width: 30,
          minWidth: 30,
        },
        {
          caption: "",
          dataField: "",
          type: "selection",
          fixed: true,
          fixedPosition: "left",
          visibleIndex: 1,
        },
        {
          screenField: "ProductItem",
          allowHiding: false,
          allowEditing: false,
          allowSorting: true,
          fixed: true,
          fixedPosition: "left",
          caption: "Product / Account",
          width: "auto",
          sortOrder: "asc",
          gridDataField: "ItemCode",
          sortIndex: 0,
          minWidth: 300,
        },
        {
          screenField: "Branch",
          cellTemplate: undefined,
          dataField: "BranchCode",
          width: ColumnWidth.BranchCode.width,
          minWidth: ColumnWidth.BranchCode.minWidth,
          allowEditing: false,
        },
        {
          screenField: "Division",
          cellTemplate: undefined,
          dataField: "DivisionCode",
          width: ColumnWidth.Division.width,
          minWidth: ColumnWidth.Division.minWidth,
          allowEditing: false,
        },
        {
          caption: "UM",
          screenField: "SalesUM",
          dataField: "SupplierUM",
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          allowEditing: false,
        },
        {
          // screenField: "Tax",
          caption: "Tax",
          dataField: "TaxCode",
          width: ColumnWidth.TaxCode.width,
          minWidth: ColumnWidth.TaxCode.minWidth,
          allowEditing: false,
        },
        {
          screenField: "SalesUMFXPrice",
          caption: `Unit Price (${this.dataModel.SupplierCurrencyCode})`,
          dataField: "SupplierFXPrice",
          format: "Decimal-16-4C",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          screenField: "Ordered",
          dataField: "SupplierRequired",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.maxWidth,
          caption: "Ordered",
          allowEditing: false,
          format: "Decimal-16-4",
        },

        {
          screenField: "Quantity",
          dataField: "SupplierOutstandingQuantity",
          format: "Decimal-16-4",
          caption: "Outstanding",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.maxWidth,
          allowEditing: false,
        },
        {
          screenField: "Quantity",
          dataField: "SupplierReceivedQty",
          caption: "Received",
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          // type: "EditableDecimal",
          allowEditing: true,
          showEditorAlways: false,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.maxWidth,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e, cellInfo) => {
              e.event.target.select();
              this.onFocusInSupplierReceivedQty(e, cellInfo);
            },
            onKeyDown: this.onSupplierReceivedQtyKeyDown,
          },
          setCellValue: async (newData, value, currentRowData) => {
            // this.onRequiredQtyValidations(newData, value, currentRowData);

            newData.SupplierReceivedQty = value;

            // FDT70189: a rebate/GL line has RequiredQty < 0, so every legal
            // Received entry is negative too - "value > 0" never includes the
            // line, unchecking it (and wiping the entry) on any partial receipt.
            const hasReceivedQty =
              currentRowData.RequiredQty < 0 ? value < 0 : value > 0;

            if (hasReceivedQty) {
              this.selectRows([currentRowData.LineNo]);
            } else {
              this.deselectRows([currentRowData.LineNo]);
            }

            if (currentRowData.IsBatchProduct == "No") {
              if (currentRowData.RequiredQty < 0) {
                if (value <= currentRowData.SupplierOutstandingQuantity)
                  newData.Completed = true;
                else newData.Completed = false;
              } else if (value < currentRowData.SupplierOutstandingQuantity) {
                newData.Completed = false;
              } else {
                newData.Completed = true;
              }
            } else {
              if (currentRowData.CheckInclude) {
                if (
                  currentRowData.SupplierReceivedQty >=
                  currentRowData.SupplierOutstandingQuantity
                ) {
                  newData.Completed = true;
                }
              }
            }

            let rowIndex = this.getDataGridInstance().getRowIndexByKey(
              currentRowData.LineNo,
            );

            if (currentRowData.RequiredQty >= 0 && value < 0) {
              let refocus = await this.$alert(
                `${currentRowData.ItemDescription} has a Required quantity that is greater than 0.00. The Received quantity should be greater than 0.00.`,
                "Validation Errors",
              );
              if (refocus) {
                newData.SupplierReceivedQty =
                  currentRowData.SupplierReceivedQty;
                this.focusOnEditingCell(rowIndex, "SupplierReceivedQty");
              }
            } else if (currentRowData.RequiredQty < 0 && value > 0) {
              let refocus = await this.$alert(
                `${currentRowData.ItemDescription} has a Required quantity that is less than 0.00. The Received quantity should be less than 0.00.`,
                "Validation Errors",
              );
              if (refocus) {
                newData.SupplierReceivedQty =
                  currentRowData.SupplierReceivedQty;
                this.focusOnEditingCell(rowIndex, "SupplierReceivedQty");
              }
            } else if (
              currentRowData.RequiredQty > 0 &&
              value > currentRowData.SupplierOutstandingQuantity
            ) {
              let refocus = await this.$alert(
                `${
                  currentRowData.ItemDescription
                } has a Received quantity of ${formatNumber(
                  value,
                )} which is more than the Outstanding quantity of ${formatNumber(
                  currentRowData.SupplierOutstandingQuantity,
                )}. Please update the Received quantity if you do not wish to over-receive against this line.`,
                "Validation Errors",
              );

              if (refocus) {
                newData.SupplierReceivedQty = value;
                this.focusOnEditingCell(rowIndex, "SupplierReceivedQty");
              }
            } else if (
              currentRowData.RequiredQty < 0 &&
              value < currentRowData.SupplierOutstandingQuantity
            ) {
              let refocus = await this.$alert(
                `${
                  currentRowData.ItemDescription
                } has a Received quantity of ${formatNumber(
                  value,
                )} which is less than the Outstanding quantity of ${formatNumber(
                  currentRowData.SupplierOutstandingQuantity,
                )}. Please update the Received quantity if you do not wish to over-receive against this line.`,
                "Validation Errors",
              );
              if (refocus) {
                newData.SupplierReceivedQty = value;
                this.focusOnEditingCell(rowIndex, "SupplierReceivedQty");
              }
            } else {
              newData.SupplierReceivedQty = value;
            }

            newData.ExFXTotal = (
              newData.SupplierReceivedQty * currentRowData.SupplierFXPrice
            ).roundNumber(2);

            if (currentRowData.TaxPcnt != 0) {
              newData.TaxFXTotal = (
                newData.ExFXTotal *
                (1 / currentRowData.TaxPcnt)
              ).roundNumber(2);

              newData.IncTaxFXTotal = (
                parseFloat(
                  newData.SupplierReceivedQty * currentRowData.SupplierFXPrice,
                ) + parseFloat(newData.ExFXTotal * (1 / currentRowData.TaxPcnt))
              ).roundNumber(2);
            } else {
              newData.TaxFXTotal = 0;

              newData.IncTaxFXTotal = parseFloat(
                newData.SupplierReceivedQty * currentRowData.SupplierFXPrice,
              ).roundNumber(2);
            }

            return newData;
          },
        },
        {
          screenField: "FXLineTotal",
          caption: `Ex Total (${this.dataModel.SupplierCurrencyCode})`,
          dataField: "ExFXTotal",
          format: "Decimal-16-2C",
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowEditing: false,
          // visible: false,
        },
        {
          screenField: "TaxFXTotal",
          caption: `Tax (${this.dataModel.SupplierCurrencyCode})`,
          format: "Decimal-16-2C",
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowEditing: false,
          // visible: false,
        },
        {
          screenField: "IncTaxFXTotal",
          caption: `Inc Total (${this.dataModel.SupplierCurrencyCode})`,
          format: "Decimal-16-2C",
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowEditing: false,
          // visible: false,
        },
        {
          screenField: "Comments",
          dataField: "Comment",
          width: 400,
          minWidth: 400,
          // cellTemplate: "ScrollView",
          // cssClass: "grid-text-scroll",
          caption: "PO Comments",
          allowEditing: false,
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
          // visible: false,
        },
        {
          dataField: "GoodsInDetailComment",
          screenField: "Comments",
          width: 400,
          minWidth: 400,
          // cellTemplate: "ScrollView",
          cssClass: "edit-cell-textbox-field comment-text",
          caption: "Goods In Comment",
          allowEditing: true,
          showEditorAlways: false,
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: this.onGoodsInDetailCommentKeyDown,
            maxLength: 100,
            maxHeight: 103,
            height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
          },
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
        },
        {
          screenField: "IsDefault",
          dataField: "Completed",
          label: "",
          editCellTemplate: "complete-template",
          cellTemplate: "complete-template",
          headerCellTemplate: "complete-header-template",
          width: ColumnWidth.CheckBoxColumn.width,
          minWidth: ColumnWidth.CheckBoxColumn.minWidth,
          cssClass: "column-checkBox",
          allowSorting: false,
          allowSearch: false,
          allowEditing: false,
          showEditorAlways: false,
          fixed: true,
          fixedPosition: "right",
          setCellValue: async (newData, value, currentRowData) => {
            newData.Completed = value;
            this.localDataSource.update(currentRowData.LineNo, newData);

            return newData;
          },
        },

        {
          screenField: "GridRecordEllipsisMenu",
          caption: "",
          allowEditing: false,
          showInColumnChooser: false,
          allowSorting: false,
          allowSearch: false,
          cssClass: "padding-7",
          visible: FeatureStorage.checkPermission("GoodsInEditPrice", "edit"),
          ellipsisMenu: {
            itemAction: () => {
              var items = [
                {
                  menuText: "Edit Item",
                  clickAction: this.enableEditGoodsInItem,
                  visible: FeatureStorage.checkPermission(
                    "GoodsInEditPrice",
                    "edit",
                  ),
                },
              ];
              return items;
            },
            keyExpr: "menuText",
            displayExpr: "menuText",
          },
          width: 40,
          minWidth: 40,
        },
      ]);
    },
    getLocalDataSource() {
      return this.localDataSource;
    },
    getGridHeight() {
      return this.gridHeight - 115;
    },

    // Footer
    getFooterItems() {
      return [
        {
          screenField: "Quantity",
          dataField: "SupplierReceivedQty",
          location: "left",
          label: "Item Count",
          value: 0,
          format: "Decimal-16-0",
        },

        {
          screenField: "SalesUMItemCount",
          dataField: "SupplierRequired",
          location: "left",
          label: "Line Count",
          value: 0,
          customCalc: () => {
            var count = 0;
            this.localDataSource._array.forEach((element) => {
              if (element.CheckInclude) count++;
            });

            return count;
          },
        },
        {
          dataField: "ExFXTotal",
          screenField: "TaxableFXTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-2C",
          label: `Sub Total (${this.dataModel.SupplierCurrencyCode})`,
        },
        {
          screenField: "TaxFXTotal",
          dataField: "TaxFXTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-2C",
          label: `Tax (${this.dataModel.SupplierCurrencyCode})`,
        },
        {
          screenField: "FXTotal",
          dataField: "IncTaxFXTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-2C",
          label: `Total (${this.dataModel.SupplierCurrencyCode})`,
        },
      ];
    },

    footerLeftItems() {
      var leftItems = this.footerItems.filter((x) => x.location == "left");

      return leftItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };
          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;
          if (c.fieldType != undefined) globalField.fieldType = c.fieldType;
          if (c.length != undefined) globalField.length = c.length;
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.dataModel.CurrencyCode})`,
            );
          }
          // if(c.length != undefined) globalField.length = c.length;
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.dataModel.CurrencyCode})`,
            );
          }
          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    footerRightItems() {
      // return this.footerItems.filter((x) => x.location == "right");
      var rightItems = this.footerItems.filter(
        (x) =>
          x.location == "right" &&
          (x.hasOwnProperty("visible") ? x.visible : true),
      );

      return rightItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };

          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;

          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.dataModel.CurrencyCode})`,
            );
          }

          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },

    getDisableCompleteHeader() {
      if (this.selectedRowKeys.length == 0) return true;
      else {
        let completeItems = this.localDataSource._array.filter(
          (x) => x.Completed,
        );
        if (
          completeItems.length == this.localDataSource._array.length ||
          this.selectedRowKeys.length == completeItems.length
        )
          return true;
        else return false;
      }
    },
    getLabel() {
      return this.expandChildGrid ? "Collapse" : "Expand";
    },
  },
  methods: {
    handleIconHover(e) {
      this.$refs[this.ProdBatchExpRefName].instance.show(e.target);
    },
    handleIconLeave() {
      this.$refs[this.ProdBatchExpRefName].instance.hide();
    },
    onBatchRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.isValid === false && this.baseView.mode !== "review") {
          e.rowElement.classList.add("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt")) {
            e.rowElement.classList.add("dx-row-alt-back");
            e.rowElement.classList.remove("dx-row-alt");
          }
        } else {
          e.rowElement.classList.remove("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt-back")) {
            e.rowElement.classList.remove("dx-row-alt-back");
            e.rowElement.classList.add("dx-row-alt");
          }
        }
      }
    },
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.getDataGridInstance().updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.getDataGridInstance().updateDimensions();
      }
    },
    async initialized() {
      if (this.baseView.mode != "review") {
        this.dataModel.SalesOrderCode = this.baseView.id;
        // this.dataModel.SalesOrderSupplierName = this.baseView.SupplierName;
        await this.setActiveAccountingPeriod();
        let response = await GoodsInListService.getPurchaseOrderByCode(
          parseInt(this.baseView.id),
        );

        this.dataModel.SupplierName = response.data.value[0].SupplierName;
        this.dataModel.SupplierCode = response.data.value[0].SupplierCode;
        this.dataModel.ComputedCode = response.data.value[0].ComputedCode;
        this.dataModel.DateChanged = response.data.value[0].DateChanged;

        await this.getPurchaseOrderDetail();

        await SupplierService.getByCode(this.dataModel.SupplierCode, {
          select: "CurrencyCode,Code",
        }).then((res) => {
          if (res.data.value.length > 0) {
            this.dataModel.SupplierCurrencyCode =
              res.data.value[0].CurrencyCode;
          }
        });

        let settingResponse = await SettingsService.getBySettingName(
          "GoodsInValidateShelfLife",
        );

        if (settingResponse.data.value.length > 0) {
          this.goodsInValidateShelfLife =
            settingResponse.data.value[0].SettingValue === "Y";
        }

        await this.$store.dispatch("screenFactors/getFactors", [507]);
        const labelPrintSetting = getFactorByCode(507);

        if (labelPrintSetting) {
          // this.goodsInLabelPrintEnabled = true; // for development only
          this.goodsInLabelPrintEnabled = labelPrintSetting.Factorc === "Y";
        }

        this.loaded = true;
        this.showLoading(false);
      } else {
        this.loaded = true;
        this.showLoading(false);
      }
    },
    async getPurchaseOrderDetail() {
      this.localDataSource.clear();
      let purchaseOrderDetails = await GoodsInService.getPurchaseOrderDetail(
        parseInt(this.baseView.id),
      );

      this.purchaseOrderDetail = {
        ...purchaseOrderDetails.data.value[0],
      };

      purchaseOrderDetails.data.value.forEach((element) => {
        element["ExFXTotal"] = 0;
        element["TaxFXTotal"] = 0;
        element["IncTaxFXTotal"] = 0;
        element["GoodsInComment"] = element["Comment"];
        if (element["PurchaseDescription"])
          element["ItemDescription"] = element["PurchaseDescription"];

        element["CheckInclude"] = false;
        element["SupplierReceivedQty"] = 0;
        element["Completed"] = false;

        if (element.IsBatchProduct != "No") {
          this.IsBatchAvailable = true;
          // Batched
          if (
            element.IsBatchProduct == "NextNo" ||
            element.IsBatchProduct == "Auto"
          ) {
            element["ChildDataSource"] = [
              {
                BatchNo: "System Generated",
                SupplierBatchNo: "",
                SupplierCode: "",
                UseByDate: null,
                SupplierBatchReceivedQty: 0,
                ActualStockConversion: element.SupplierConversion,
                ReceivedQty: 0, // element.ReceivedQty,
                SupplierOutstandingQuantity:
                  element.SupplierOutstandingQuantity,
                IsManufacturedDateRequired: element.IsManufacturedDateRequired,
                UseByShelfLife: element.UseByShelfLife,
                BestBeforeShelfLife: element.BestBeforeShelfLife,
                IsBatchProduct: element.IsBatchProduct,
                BestBeforeDate: undefined,
                ManufacturedDate: undefined,
                ParentLineNo: element.LineNo,
                LineNo: parseInt(element.LineNo.toString() + "0") * 100,
              },
            ];
          } else {
            if (element.IsBatchProduct == "Manual") {
              // element["GoodsInProductBatches"] = [];
              element["ChildDataSource"] = [
                {
                  BatchNo: "",
                  SupplierBatchNo: "",
                  SupplierCode: "",
                  UseByDate: null,
                  SupplierBatchReceivedQty: 0,
                  ActualStockConversion: element.SupplierConversion,
                  ReceivedQty: 0, // element.ReceivedQty,
                  SupplierOutstandingQuantity:
                    element.SupplierOutstandingQuantity,
                  IsManufacturedDateRequired:
                    element.IsManufacturedDateRequired,
                  UseByShelfLife: element.UseByShelfLife,
                  BestBeforeShelfLife: element.BestBeforeShelfLife,
                  IsBatchProduct: element.IsBatchProduct,
                  BestBeforeDate: undefined,
                  ManufacturedDate: undefined,
                  ParentLineNo: element.LineNo,
                  LineNo: parseInt(element.LineNo.toString() + "0") * 100,
                },
              ];
            } else {
              element["SupplierReceivedQty"] =
                element["SupplierOutstandingQuantity"];
            }
          }
        }

        this.localDataSource.insert(element);
      });
    },
    async setActiveAccountingPeriod() {
      let startDateResult =
        await GoodsInService.getActiveAccountingPeriodStartDate();
      if (startDateResult) {
        let accountingPeriod = startDateResult.data.value[0];
        this.ui.DateInMin = new Date(accountingPeriod.StartDate);
      }
      let endDateResult =
        await GoodsInService.getActiveAccountingPeriodEndDate();
      if (endDateResult) {
        let accountingPeriod = endDateResult.data.value[0];
        this.ui.DateInMax = new Date(accountingPeriod.EndDate);
      }
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          // this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    formContentReady(e) {
      if (e && this.baseView.mode != "review" && this.loaded)
        setTimeout(() => {
          this.headerToolBarForm = e.component;
          if (
            this.headerToolBarForm &&
            this.headerToolBarForm.getEditor("DateIn")
          )
            this.headerToolBarForm.getEditor("DateIn").instance().focus();
        }, 500);
    },

    async saveTransaction(isValid, brokenRules) {
      const headerToolbarForm = await validationEngine.validateGroup(
        "gc-form-header-toolbar",
      );

      if (!headerToolbarForm.isValid) {
        isValid = headerToolbarForm.isValid;
        brokenRules = headerToolbarForm.brokenRules.concat(brokenRules);
      }

      if (!isValid) this.displayValidationSummary(brokenRules);
      else {
        this.saveGoodsIn(this.dataModel);
      }
    },
    async saveGoodsIn(dataModel) {
      let gridItems = this.localDataSource._array;

      let manualBatchValidation = true;
      let manufacturedDateRequire = false;
      let payload = {
        DateIn: dataModel.DateIn
          ? dataModel.DateIn.toSaveDate()
          : dataModel.DateIn,
        DateReceived: dataModel.DateReceived
          ? dataModel.DateReceived.toSaveDate()
          : dataModel.DateReceived,
        SupplierCode: dataModel.SupplierCode,
        GoodsInDetails: [],
        DateChanged: dataModel.DateChanged
          ? new Date(dataModel.DateChanged).toISOString().replace("Z", "")
          : null,
      };

      gridItems.forEach((el) => {
        if (el.CheckInclude) {
          let obj = {
            ActualStockConversion: el.SupplierConversion,
            Comment: el.GoodsInDetailComment,
            IsVariable: el.IsVariable,
            ItemCode: el.ItemCode,
            LineComplete: el.Completed ? "Y" : "N",
            PurchaseOrderCode: el.PurchaseOrderCode,
            PurchaseOrderComment: el.Comment,
            PurchaseOrderLineNo: el.LineNo,
            ReceivedQty: el.SupplierReceivedQty * el.SupplierConversion,
            StandardStockConversion: el.SupplierConversion,
            SupplierReceivedQty: el.SupplierReceivedQty,
            GoodsInProductBatches: [],
          };

          if (el.ChildDataSource) {
            el.ChildDataSource.forEach((i) => {
              let objBatch = {
                ActualStockConversion: i.ActualStockConversion,
                BatchNo: i.BatchNo,
                Comment: el.GoodsInDetailComment,
                ReceivedQty:
                  i.SupplierBatchReceivedQty * i.ActualStockConversion,
                SupplierBatchNo: i.SupplierBatchNo,
                SupplierReceivedQty: i.SupplierBatchReceivedQty,
                UseByDate: i.UseByDate ? i.UseByDate.toSaveDate() : i.UseByDate,
                BestBeforeDate: i.BestBeforeDate
                  ? i.BestBeforeDate.toSaveDate()
                  : i.BestBeforeDate,
                ManufacturedDate: i.ManufacturedDate
                  ? i.ManufacturedDate.toSaveDate()
                  : i.ManufacturedDate,
              };
              obj.GoodsInProductBatches.push(objBatch);
            });
          } else {
            delete obj.GoodsInProductBatches;
          }

          if (el.IsBatchProduct == "Manual") {
            el.ChildDataSource.forEach((batch) => {
              if (!batch.BatchNo && batch.SupplierBatchReceivedQty != 0)
                manualBatchValidation = false;

              if (
                batch.SupplierBatchReceivedQty != 0 &&
                batch.IsManufacturedDateRequired == "Y" &&
                !batch.ManufacturedDate
              ) {
                manufacturedDateRequire = true;
              }
            });
          }
          payload.GoodsInDetails.push(obj);
        }
      });

      if (payload.GoodsInDetails.length == 0) {
        this.validationSummary = [];
        this.validationSummary.push({
          message: "You must have at least one line received.",
        });

        this.displayValidationSummary(this.validationSummary);
        return;
      }

      if (manualBatchValidation && !manufacturedDateRequire) {
        this.showLoading(true);
        await GoodsInService.saveGoodsIn(payload)
          .then((res) => {
            this.showLoading(false);
            this.$notify(
              `Successfully created Goods In ${res.data.Code}`,
              "success",
            );

            this.setIgnoreRouteChangeConfirmation();
            const hasStockLine = gridItems.some(
              (el) => el.CheckInclude && el.ItemType !== "G",
            );
            if (this.goodsInLabelPrintEnabled && hasStockLine) {
              this.labelPrintingTransactionNo = res.data.Code;
              this.labelPrintingVisible = true;
            } else {
              this.$router.push({
                path: `/goods-in`,
              });
            }

            this.$logger.debug("response", res);
          })
          .catch((error) => {
            this.showLoading(false);
            this.serverError = {
              err: error,
              msg: "Goods In save failed",
              time: new Date().getTime(),
            };
          });
      } else {
        this.validationSummary = [];
        let error = [];

        if (manufacturedDateRequire)
          error.push({ message: "Manufactured Date is Required" });

        if (!manualBatchValidation)
          error.push({
            message:
              "Batch Numbers are required for products that have a Manual batch method.",
          });
        this.displayValidationSummary(error);
      }
    },
    selectRows(keys) {
      let instance = this.getDataGridInstance();
      if (instance) {
        keys.forEach((element) => {
          this.localDataSource.update(element, { CheckInclude: true });
        });
        instance.selectRows(keys, true);
      }
    },
    deselectRows(keys) {
      let instance = this.getDataGridInstance();
      if (instance) {
        keys.forEach((element) => {
          this.localDataSource.update(element, { CheckInclude: false });
        });
        instance.deselectRows(keys);
      }
    },
    getDataGridInstance() {
      return this.$refs["goods-in-grid"].instance;
    },
    getMasterDataInstance(index) {
      return this.$refs["master-details-" + index].instance;
    },
    focusOnEditingCell(rowIndex, dataField) {
      setTimeout(() => {
        let gridInstance = this.getDataGridInstance();
        if (gridInstance) {
          let cellElement = gridInstance.getCellElement(rowIndex, dataField);
          if (cellElement) {
            gridInstance.focus(cellElement);
            setTimeout(() => {
              gridInstance.editCell(rowIndex, dataField);
            }, 1);
          }
        }
      }, 10);
    },
    onCompleteHeaderUpdate(e) {
      if (!e.event) return;

      this.localDataSource._array.forEach((element) => {
        if (element.CheckInclude && element.IsBatchProduct == "No") {
          if (e.value) {
            if (
              e.previousValue == undefined &&
              element.Completed &&
              element.SupplierReceivedQty != element.SupplierOutstandingQuantity
            )
              element.Completed = false;
            else element.Completed = e.value;
          } else {
            if (
              element.SupplierReceivedQty != element.SupplierOutstandingQuantity
            )
              element.Completed = e.value;
          }
        }
      });
    },
    setUpdateHeaderComplete() {
      setTimeout(() => {
        let dsItems = this.localDataSource._array;
        let completeItems = dsItems.filter((x) => x.Completed);

        if (!this.$refs["complete-header-template"]) return;

        var refHeadInstance = this.$refs["complete-header-template"].instance;
        refHeadInstance.blur();
        var checkBoxHeader = refHeadInstance.element();
        var getInstanceHeader = CheckBox.getInstance(checkBoxHeader);

        if (completeItems.length == 0) getInstanceHeader.option("value", false);
        else if (completeItems.length == dsItems.length)
          getInstanceHeader.option("value", true);
        else getInstanceHeader.option("value", undefined);
      }, 1);
    },
    // Cell Updates
    async onRequiredQtyValidations(newData, value, currentRowData) {
      this.localDataSource.update(currentRowData.LineNo, newData);
    },
    getLineCompleteStatus(data) {
      if (data && data.CheckInclude == false) {
        return true;
      } else if (data.RequiredQty < 0) {
        if (data.SupplierReceivedQty <= data.SupplierOutstandingQuantity)
          return true;
        else return false;
      } else if (
        data &&
        data.SupplierReceivedQty >= data.SupplierOutstandingQuantity
      ) {
        return true;
      } else if (
        data &&
        data.SupplierReceivedQty < data.SupplierOutstandingQuantity
      ) {
        return false;
      } else return false;
    },

    // Data Grid Events
    onCellPrepared(e) {
      var self = this;
      if (e.column.type === "selection") {
        on(e.cellElement, "mouseover", (arg) => {
          self.$refs[this.tooltipRefName].instance.show(arg.target);
        });

        on(e.cellElement, "mouseout", () => {
          self.$refs[this.tooltipRefName].instance.hide();
        });
      }

      if (e.rowType === "data" || e.rowType === "header") {
        if (e.column.dataField === "Completed") {
          on(e.cellElement, "mouseover", (arg) => {
            self.$refs[this.CompleteTooltipRefName].instance.show(arg.target);
          });

          on(e.cellElement, "mouseout", () => {
            self.$refs[this.CompleteTooltipRefName].instance.hide();
          });
        }
      }

      if (e.rowType === "data" && e.column.command === "expand") {
        if (e.data.IsBatchProduct == "No") {
          if (e.cellElement && e.cellElement.classList) {
            e.cellElement.classList.remove("dx-datagrid-expand");
            e.cellElement.childNodes[0].classList.remove(
              "dx-datagrid-group-closed",
            );
          }
        }
      }
    },
    onSelectionChanged(e) {
      let rowIndexs = [];
      if (e.currentSelectedRowKeys.length > 0) {
        e.currentSelectedRowKeys.forEach((element) => {
          let newData = e.selectedRowsData.find((x) => x.LineNo == element);

          if (
            newData.SupplierReceivedQty != newData.SupplierOutstandingQuantity
          ) {
            if (newData.IsBatchProduct == "No") {
              newData.SupplierReceivedQty = newData.SupplierOutstandingQuantity;
              newData.Completed = true;
            } else {
              if (!newData.CheckInclude) {
                newData.SupplierReceivedQty =
                  newData.SupplierOutstandingQuantity;
                newData.Completed = true;
              }
            }
          }
          newData.CheckInclude = true;

          newData.ExFXTotal = (
            newData.SupplierReceivedQty * newData.SupplierFXPrice
          ).roundNumber(2);

          if (newData.TaxPcnt != 0) {
            newData.TaxFXTotal = (
              newData.ExFXTotal *
              (1 / newData.TaxPcnt)
            ).roundNumber(2);

            newData.IncTaxFXTotal = (
              parseFloat(
                newData.SupplierReceivedQty * newData.SupplierFXPrice,
              ) + parseFloat(newData.ExFXTotal * (1 / newData.TaxPcnt))
            ).roundNumber(2);
          } else {
            newData.TaxFXTotal = 0;

            newData.IncTaxFXTotal = parseFloat(
              newData.SupplierReceivedQty * newData.SupplierFXPrice,
            ).roundNumber(2);
          }

          if (newData.IsBatchProduct != "No") {
            newData.ChildDataSource.forEach((element) => {
              element.SupplierBatchReceivedQty =
                newData.SupplierReceivedQty / newData.ChildDataSource.length;
            });
          }

          this.localDataSource.update(newData.LineNo, newData).then(() => {
            let rowIndex = e.component.getRowIndexByKey(newData.LineNo);
            rowIndexs.push(rowIndex);
          });
        });
      }

      if (e.currentDeselectedRowKeys.length > 0) {
        e.currentDeselectedRowKeys.forEach((element) => {
          e.component.byKey(element).then((res) => {
            let newData = res;
            newData.SupplierReceivedQty = 0;
            newData.Completed = false;
            newData.CheckInclude = false;
            newData.ExFXTotal = 0;
            newData.TaxFXTotal = 0;
            newData.IncTaxFXTotal = 0;

            if (newData.IsBatchProduct != "No") {
              newData.ChildDataSource.forEach((element) => {
                element.SupplierBatchReceivedQty = 0;
              });
            }

            this.localDataSource.update(newData.LineNo, newData).then(() => {
              let rowIndex = e.component.getRowIndexByKey(newData.LineNo);
              rowIndexs.push(rowIndex);
            });
          });
        });
      }

      e.component.repaintRows(rowIndexs);
    },
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      searchPanel.location = "after";

      e.toolbarOptions.items.push({
        location: "before",
        widget: "dxButton",
        options: {
          icon: "plus",
          text: "Add Item",
          onClick: () => {
            this.enableAddGoodsInOrderItem();
          },
          visible: FeatureStorage.checkPermission("GoodsInEditPrice", "edit"),
        },
      });

      e.toolbarOptions.items.push({
        location: "before",
        widget: "dxButton",
        options: {
          icon: this.expandChildGrid ? "chevronup" : "chevrondown",
          text: this.getLabel,
          onClick: () => {
            this.expandMasterDetailsSection();
          },
          onContentReady: (e) => {
            this.CollapseBtnContentReady = e;
          },
          visible: this.IsBatchAvailable,
        },
      });
    },
    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              data: e.row,
            });
          };
      }

      if (e.editorOptions && e.editorOptions.onFocusIn) {
        let func = e.editorOptions.onFocusIn;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onFocusIn = (event) => {
            func(event, {
              data: e.row,
            });
          };
      }

      if (e.dataField && e.dataField == "GoodsInDetailComment") {
        e.editorName = "dxTextArea";
      }
    },
    onContentReady(e) {
      if (e && this.gridDataLoaded == false) {
        this.gridDataLoaded = true;
        setTimeout(() => {
          this.expandMasterDetailsSection();
        }, 500);
      }
    },
    onEditingStart(e) {
      let data = e.data;

      if (e.column.dataField === "SupplierReceivedQty") {
        if (data.IsBatchProduct != "No") {
          e.cancel = true;
        }
      }
    },

    // Batch Grid
    getChildGridColumns(data) {
      let item = data.data;

      let visibleUseBy = true;
      let visibleBestBefore = true;
      // let changeWidth = 0;

      if (item.UseByShelfLife == -1 && item.BestBeforeShelfLife == -1) {
        visibleUseBy = false;
        visibleBestBefore = false;
        // changeWidth = ColumnWidth.Date.width / 2;
      } else if (item.UseByShelfLife > -1 && item.BestBeforeShelfLife > -1) {
        visibleBestBefore = false;
      } else if (
        item.UseByShelfLife > -1
        // && item.BestBeforeShelfLife != -1
      ) {
        visibleBestBefore = false;
      } else if (
        item.BestBeforeShelfLife > -1
        // && item.UseByShelfLife != -1
      ) {
        visibleUseBy = false;
      }

      return this.parseColumnWithScreenFields([
        {
          screenField: "Batch",
          dataField: "BatchNo",
          allowEditing: item.IsBatchProduct == "Manual",
          showEditorAlways: false,
          width: ColumnWidth.BatchNo.width + 260,
          minWidth: ColumnWidth.BatchNo.minWidth + 260,
          editorOptions: {
            onKeyDown: this.onBatchNoKeyDown,
            maxLength: 20,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          cssClass: "edit-cell-textbox-field",
          setCellValue: async (newData, value, currentRowData) => {
            let lineParent = this.localDataSource._array.find(
              (x) => x.LineNo == currentRowData.ParentLineNo,
            );

            const batchValues = lineParent.ChildDataSource.filter(
              (x) => x.BatchNo.toLowerCase() == value.toLowerCase(),
            );

            if (batchValues.length > 0) {
              await this.$alert(
                `Batch No ${value} already exists for this product, choose a different batch number.`,
                "Validation Error",
              );

              newData.BatchNo = currentRowData.BatchNo;

              setTimeout(() => {
                this.refocusOnBatchGridEditableCell(currentRowData, "BatchNo");
              }, 100);
            } else {
              newData.BatchNo = value;
              await GoodsInService.getGoodsInBatches(
                `ItemCode eq '${lineParent.ItemCode}' and SupplierCode eq '${this.dataModel.SupplierCode}' and BatchNo eq '${value}'`,
              ).then((res) => {
                if (res.data.value.length > 0) {
                  let data = res.data.value[0];
                  newData.SupplierBatchNo = data.SupplierBatchNo;
                  if (data.ManufacturedDate)
                    newData.ManufacturedDate = new Date(data.ManufacturedDate);
                  if (data.BestBeforeDate)
                    newData.BestBeforeDate = new Date(data.BestBeforeDate);
                  if (data.UseByDate)
                    newData.UseByDate = new Date(data.UseByDate);

                  newData.FromBatchDB = true;
                } else {
                  newData.FromBatchDB = false;
                }

                let dataField = undefined;
                if (newData.FromBatchDB) {
                  if (this.batchNoTabKey) {
                    this.batchNoTabKey = false;
                    dataField = "SupplierBatchReceivedQty";
                  }
                } else {
                  if (this.batchNoTabKey) {
                    this.batchNoTabKey = false;
                    dataField = "SupplierBatchNo";
                  }
                }

                if (dataField) {
                  let rowIndex = this.BatchGridInstance.getRowIndexByKey(
                    currentRowData.LineNo,
                  );
                  let cellElement = this.BatchGridInstance.getCellElement(
                    rowIndex,
                    dataField,
                  );
                  if (cellElement) {
                    this.BatchGridInstance.focus(cellElement);
                    setTimeout(() => {
                      this.BatchGridInstance.editCell(rowIndex, dataField);
                    }, 100);
                  }
                }
                return newData;
              });
            }

            return newData;
          },
        },
        {
          screenField: "SupplierBatchNo",
          cssClass: "edit-cell-textbox-field",
          allowEditing: true,
          width: ColumnWidth.SupplierBatchNo.width + 308,
          minWidth: ColumnWidth.SupplierBatchNo.minWidth + 260,
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: this.onSupplierBatchNoKeyDown,
          },
        },
        {
          screenField: "Quantity",
          dataField: "SupplierOutstandingQuantity",
          format: "Decimal-16-4",
          label: "Outstanding",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.maxWidth,
        },
        {
          screenField: "Quantity",
          dataField: "SupplierBatchReceivedQty",
          format: "##,###,###,###,##0.0000",
          cssClass: "edit-cell-numberbox-field",
          showEditorAlways: false,
          allowEditing: true,
          caption: "Received",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.maxWidth,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: this.onSupplierBatchReceivedQtyDown,
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.SupplierBatchReceivedQty = value;
            let instance = this.getDataGridInstance();

            let lineParent = this.localDataSource._array.find(
              (x) => x.LineNo == currentRowData.ParentLineNo,
            );

            let totalBatchPickedQty = 0;
            lineParent.ChildDataSource.forEach((x) => {
              totalBatchPickedQty += x.SupplierBatchReceivedQty
                ? x.SupplierBatchReceivedQty
                : 0;
            });

            lineParent.SupplierReceivedQty =
              totalBatchPickedQty -
              currentRowData.SupplierBatchReceivedQty +
              newData.SupplierBatchReceivedQty;
            newData.CheckInclude = lineParent.CheckInclude;

            if (newData.CheckInclude) {
              // include checked
              if (
                lineParent.SupplierReceivedQty >=
                lineParent.SupplierOutstandingQuantity
              ) {
                lineParent.Completed = true;
              } else {
                lineParent.Completed = false;
              }
            } else {
              // include is not checked
              lineParent.CheckInclude = true;
              if (
                newData.SupplierBatchReceivedQty >=
                lineParent.SupplierOutstandingQuantity
              ) {
                lineParent.Completed = true;
              } else {
                lineParent.Completed = false;
              }
            }

            if (
              lineParent.SupplierReceivedQty >
              lineParent.SupplierOutstandingQuantity
            ) {
              this.$nextTick(async function () {
                let alert = await this.$alert(
                  `Item : ${
                    lineParent.ItemDescription
                  } has a received quantity of ${formatNumber(
                    value,
                  )} which is more than the outstanding quantity of ${formatNumber(
                    lineParent.SupplierOutstandingQuantity,
                  )}.`,
                  "Validation Errors",
                );

                if (alert) {
                  let batchGridInstance = this.getMasterDataInstance(
                    currentRowData.ParentLineNo,
                  );
                  if (batchGridInstance) {
                    let batchRowIndex = batchGridInstance.getRowIndexByKey(
                      currentRowData.LineNo,
                    );
                    if (batchRowIndex != -1) {
                      let cellElement = batchGridInstance.getCellElement(
                        batchRowIndex,
                        "SupplierBatchReceivedQty",
                      );
                      if (cellElement) {
                        batchGridInstance.focus(cellElement);
                        setTimeout(() => {
                          batchGridInstance.editCell(
                            batchRowIndex,
                            "SupplierBatchReceivedQty",
                          );
                        }, 1);
                      }
                    }
                  }
                }
              });
            }

            // Cal Line Totals
            lineParent.ExFXTotal = (
              lineParent.SupplierReceivedQty * lineParent.SupplierFXPrice
            ).toFixed(2);

            if (lineParent.TaxPcnt != 0) {
              lineParent.TaxFXTotal = (
                lineParent.ExFXTotal *
                (1 / lineParent.TaxPcnt)
              ).toFixed(2);

              lineParent.IncTaxFXTotal =
                parseFloat(
                  lineParent.SupplierReceivedQty * lineParent.SupplierFXPrice,
                ) + parseFloat(lineParent.ExFXTotal * (1 / lineParent.TaxPcnt));
            } else {
              lineParent.TaxFXTotal = 0;

              lineParent.IncTaxFXTotal = parseFloat(
                lineParent.SupplierReceivedQty * lineParent.SupplierFXPrice,
              );
            }

            this.localDataSource
              .update(lineParent.LineNo, lineParent)
              .then((res) => {
                if (res.SupplierReceivedQty != 0) {
                  this.selectRows([res.LineNo]);
                } else {
                  this.deselectRows([res.LineNo]);
                }
                let rowIndex = instance.getRowIndexByKey(res.LineNo);
                instance.repaintRows([rowIndex]);
              });

            return newData;
          },
        },
        {
          screenField: "DateIn",
          caption: "Manufactured Date",
          dataField: "ManufacturedDate",
          allowEditing: true,
          allowSearch: false,
          dataType: "date",
          editable: true,
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          format: "dd MMM yyyy",
          showEditorAlways: false,
          cssClass: "edit-cell-datebox-field-border",
          editorOptions: {
            format: "dd MMM yyyy",
            onKeyDown: this.onManufacturedDateKeyDown,
          },
          // eslint-disable-next-line no-unused-vars
          setCellValue: async (newData, value, currentRowData) => {
            newData.ManufacturedDate = value;

            if (value) {
              // if (
              //   currentRowData.UseByShelfLife > -1 &&
              //   currentRowData.BestBeforeShelfLife > -1
              // ) {
              //   newData.UseByDate = new Date(value).addDays(
              //     currentRowData.UseByShelfLife
              //   );
              // } else if (
              //   currentRowData.UseByShelfLife > -1
              //   // && item.BestBeforeShelfLife != -1
              // ) {
              //   newData.UseByDate = new Date(value).addDays(
              //     currentRowData.UseByShelfLife
              //   );
              // } else if (
              //   currentRowData.BestBeforeShelfLife > -1
              //   // && item.UseByShelfLife != -1
              // ) {
              //   newData.BestBeforeDate = new Date(value).addDays(
              //     currentRowData.BestBeforeShelfLife
              //   );
              // } else {
              //   currentRowData.UseByDate = undefined;
              //   currentRowData.BestBeforeDate = undefined;
              // }
            } else {
              newData.UseByDate = undefined;
              newData.BestBeforeDate = undefined;
            }

            return newData;
          },
        },
        {
          screenField: "UseByDate",
          allowEditing: true,
          allowSearch: false,
          showEditorAlways: false,
          cssClass: "edit-cell-datebox-field-border",
          dataType: "date",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          visible: visibleUseBy,
          format: "dd MMM yyyy",
          editorOptions: {
            format: "dd MMM yyyy",
            onKeyDown: this.onUseByDateKeyDown,
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.UseByDate = value;

            let lineParent = this.localDataSource._array.find(
              (x) => x.LineNo == currentRowData.ParentLineNo,
            );

            if (value) {
              if (this.goodsInValidateShelfLife && lineParent.MLORDays > 0) {
                let goodsInDate = new Date(this.dataModel.DateReceived);
                let shelfLifeDate = new Date(
                  goodsInDate.setDate(
                    goodsInDate.getDate() + lineParent.MLORDays,
                  ),
                );

                if (shelfLifeDate > value) {
                  newData.isValid = false;
                } else {
                  newData.isValid = true;
                }
              }
            }

            // if (new Date(value) < new Date(currentRowData.ManufacturedDate)) {
            //   this.$alert(
            //     "Use By Date can not be less than the Manufactured Date",
            //     "Validation Error"
            //   );
            //   newData.UseByDate = currentRowData.UseByDate;
            // }

            return newData;
          },
          cellTemplate: "exp-date-template",
        },
        {
          screenField: "BestBeforeDate",
          allowEditing: true,
          allowSearch: false,
          showEditorAlways: false,
          cssClass: "edit-cell-datebox-field-border",
          dataType: "date",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          visible: visibleBestBefore,
          format: "dd MMM yyyy",
          editorOptions: {
            format: "dd MMM yyyy",
            onKeyDown: this.onUseByDateKeyDown, // UseByDate & BestBeforeDate Both acts in the same way
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.BestBeforeDate = value;

            let lineParent = this.localDataSource._array.find(
              (x) => x.LineNo == currentRowData.ParentLineNo,
            );

            if (value) {
              if (this.goodsInValidateShelfLife && lineParent.MLORDays > 0) {
                let goodsInDate = new Date(this.dataModel.DateReceived);
                let shelfLifeDate = new Date(
                  goodsInDate.setDate(
                    goodsInDate.getDate() + lineParent.MLORDays,
                  ),
                );

                if (shelfLifeDate > value) {
                  newData.isValid = false;
                } else {
                  newData.isValid = true;
                }
              }
            }
            // if (new Date(value) < new Date(currentRowData.ManufacturedDate)) {
            //   this.$alert(
            //     "Use By Date can not be less than the Manufactured Date",
            //     "Validation Error"
            //   );
            //   newData.BestBeforeDate = currentRowData.BestBeforeDate;
            // }

            return newData;
          },
          cellTemplate: "exp-date-template",
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          width: 110,
          buttons: [
            {
              hint: "Add Batch Line",
              icon: "add",
              visible: true,
              onClick: this.onAddNewBatchLine.bind(this),
            },
            "delete",
          ],
        },
      ]);
    },
    refocusOnBatchGridEditableCell(currentRowData, dataField) {
      let batchGridInstance = this.getMasterDataInstance(
        currentRowData.ParentLineNo,
      );
      if (batchGridInstance) {
        let batchRowIndex = batchGridInstance.getRowIndexByKey(
          currentRowData.LineNo,
        );
        if (batchRowIndex != -1) {
          let cellElement = batchGridInstance.getCellElement(
            batchRowIndex,
            dataField,
          );
          if (cellElement) {
            batchGridInstance.focus(cellElement);
            setTimeout(() => {
              batchGridInstance.editCell(batchRowIndex, dataField);
            }, 1);
          }
        }
      }
    },
    expandMasterDetailsSection() {
      let gridInstance = this.getDataGridInstance();

      let items = this.localDataSource._array;
      if (this.expandChildGrid) {
        this.expandChildGrid = false;
        // gridInstance.collapseAll(-1);
        items.forEach((element, index) => {
          if (element.ChildDataSource && element.ChildDataSource.length > 0) {
            setTimeout(() => {
              gridInstance.collapseRow(element.LineNo);
            }, 100 * index);
          }
        });
      } else {
        this.expandChildGrid = true;
        items.forEach((element, index) => {
          if (element.ChildDataSource && element.ChildDataSource.length > 0) {
            setTimeout(() => {
              gridInstance.expandRow(element.LineNo);
            }, 100 * index);
          }
        });
      }
      setTimeout(() => {
        gridInstance.refresh(true).then(() => {
          if (this.CollapseBtnContentReady) {
            this.CollapseBtnContentReady.component.beginUpdate();
            this.CollapseBtnContentReady.component.option(
              "text",
              this.getLabel,
            );
            this.CollapseBtnContentReady.component.option(
              "icon",
              this.expandChildGrid ? "chevronup" : "chevrondown",
            );
            this.CollapseBtnContentReady.component.endUpdate();
          }
        });
      }, 100);
    },
    onAddNewBatchLine(e) {
      if (e.event) {
        let data = e.row.data;
        if (data.IsBatchProduct == "Manual" && !data.BatchNo) {
          this.$alert("Batch No is required", "Validation Error");
        } else if (
          data.IsManufacturedDateRequired == "Y" &&
          !data.ManufacturedDate
        ) {
          this.$alert("Manufactured Date is required", "Validation Error");
        } else {
          let instance = this.getDataGridInstance();
          instance.byKey(data.ParentLineNo).then((lineParent) => {
            this.addNewBatchLine(lineParent);
          });
        }
      }
    },
    getDefaultBatchNo(lineParent) {
      return lineParent.IsBatchProduct == "NextNo" ||
        lineParent.IsBatchProduct == "Auto"
        ? "System Generated"
        : "";
    },
    onBatchEditingStart(e) {
      let data = e.data;

      if (
        e.column.dataField === "ManufacturedDate" ||
        e.column.dataField === "SupplierBatchNo"
      ) {
        if (data.FromBatchDB) {
          e.cancel = true;
        }
      }

      if (
        e.column.dataField === "UseByDate" ||
        e.column.dataField === "BestBeforeDate"
      ) {
        if (data.FromBatchDB) {
          e.cancel = true;
        }
      }
    },
    onBatchRowRemoved(e) {
      let data = e.data;

      let instance = this.getDataGridInstance();
      instance.byKey(data.ParentLineNo).then((lineParent) => {
        lineParent.SupplierReceivedQty -= data.SupplierBatchReceivedQty;
        lineParent.Completed =
          lineParent.SupplierReceivedQty >=
          lineParent.SupplierOutstandingQuantity;
        if (lineParent.ChildDataSource.length === 0) {
          this.addNewBatchLine(lineParent);
        }
      });

      const rowIndex = instance.getRowIndexByKey(data.ParentLineNo);
      if (rowIndex !== -1) {
        instance.repaintRows([rowIndex]);
      }
    },
    onBatchEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              data: e.row,
              component: e.component,
            });
          };
      }

      if (e.dataField == "BatchNo") {
        if (e.row.data.IsBatchProduct == "Manual" && !e.row.data.BatchNo) {
          e.editorElement.classList.add("gc-require-validation");
        }
      }

      if (e.dataField == "ManufacturedDate") {
        if (
          e.row.data.IsManufacturedDateRequired == "Y" &&
          !e.row.data.ManufacturedDate
        ) {
          e.editorElement.classList.remove("edit-cell-datebox-field-border");
          e.editorElement.classList.add("gc-require-validation");
        }
      }
    },
    onBatchCellPrepared(e) {
      if (e.rowType == "data") {
        if (e.column.dataField == "BatchNo") {
          if (e.data.IsBatchProduct == "Manual" && !e.data.BatchNo) {
            e.cellElement.classList.add("gc-require-validation");
          }
        }

        if (e.column.dataField == "ManufacturedDate") {
          if (
            e.data.IsManufacturedDateRequired == "Y" &&
            !e.data.ManufacturedDate
          ) {
            e.cellElement.classList.add("gc-require-validation");
          }
        }
      }
    },
    onBatchFocusedCellChanged(e) {
      let gridInstance = this.getDataGridInstance();
      if (gridInstance) gridInstance.option("focusedRowIndex", -1);

      if (e) {
        this.BatchGridInstance = e.component;
      }
    },

    async enableAddGoodsInOrderItem() {
      this.addGoodsInItem = {
        visible: true,
        addSalesOrderItemData: {
          ...this.dataModel,
          DivisionCode: this.purchaseOrderDetail.DivisionCode,
          PurchaseOrderCode: this.purchaseOrderDetail.PurchaseOrderCode,
          BranchCode: this.purchaseOrderDetail.BranchCode,
          orderType: "gi",
        },
        popUpCallBack: this.addGoodsInCallback,
      };
    },
    async addGoodsInCallback(update) {
      this.addGoodsInItem = undefined;
      if (update) {
        await this.getPurchaseOrderDetail();
        this.getDataGridInstance().refresh(true);
      }
    },

    addNewBatchLine(lineParent) {
      var batchNo = this.getDefaultBatchNo(lineParent);
      var maxLineNo = Math.max(
        ...lineParent.ChildDataSource.map((e) => e.LineNo),
      );
      if (lineParent.ChildDataSource.length == 0)
        maxLineNo = parseInt(lineParent.LineNo.toString() + "0") * 100;
      lineParent.ChildDataSource.push({
        BatchNo: batchNo,
        SupplierBatchNo: "",
        SupplierCode: "",
        UseByDate: null,
        SupplierBatchReceivedQty: 0,
        ActualStockConversion: lineParent.SupplierConversion,
        ReceivedQty: lineParent.ReceivedQty,
        SupplierOutstandingQuantity: lineParent.SupplierOutstandingQuantity,
        IsManufacturedDateRequired: lineParent.IsManufacturedDateRequired,
        UseByShelfLife: lineParent.UseByShelfLife,
        BestBeforeShelfLife: lineParent.BestBeforeShelfLife,
        IsBatchProduct: lineParent.IsBatchProduct,
        BestBeforeDate: undefined,
        ManufacturedDate: undefined,
        ParentLineNo: lineParent.LineNo,
        LineNo: ++maxLineNo,
      });

      this.localDataSource.update(lineParent.LineNo, lineParent);
    },

    enableEditGoodsInItem(data) {
      this.editGoodsInItem = {
        visible: true,
        editProductItemData: {
          DivisionCode: data.DivisionCode,
          PurchaseOrderCode: data.PurchaseOrderCode,
          ItemCode: data.ItemCode,
          ItemType: data.ItemType,
          TaxCode: data.TaxCode,
          ItemDescription: data.ItemDescription,
          SalesUM: data.SupplierUM,
          SalesUMOrderQty: data.SupplierReceivedQty,
          SalesUMFXPrice: data.SupplierFXPrice,
          SalesTaxPcnt: data.TaxPcnt,
          DiscPcnt: data.DiscPcnt,
          Comment: data.Comment,
          Amount: data.FXLineTotal,
          ID: data.LineNo,
          BranchCode: data.BranchCode,
          SupplierCode: this.dataModel.SupplierCode,
          ETADate: data.ETADate,
          RequiredByDate: data.RequiredByDate,
          orderType: "gi",
        },
        popUpCallBack: this.editItemCallback,
        model: this.dataModel,
      };
    },
    async editItemCallback(update) {
      this.editGoodsInItem = undefined;
      if (update) {
        await this.getPurchaseOrderDetail();
        this.getDataGridInstance().refresh(true);
      }
    },
    onItemClickCallBack(e, item) {
      item.itemData.clickAction(e.data);
    },
    onFocusInSupplierReceivedQty(e) {
      if (e) {
        let gridInstance = this.getDataGridInstance();
        let scrollInstance = gridInstance.getScrollable();
        if (scrollInstance) {
          scrollInstance.scrollTo({ left: 0, top: 0 });
        }
      }
    },

    // Key Navigation
    onDateReceivedKeyDown(e) {
      if (e.event) {
        if (e.event.key == "Tab" && e.event.shiftKey == false) {
          e.event.preventDefault();
          let gridInstance = this.getDataGridInstance();

          let items = gridInstance.getDataSource().items();
          let dataField = undefined;
          if (items.length > 0) {
            if (items[0].IsBatchProduct == "No") {
              dataField = "SupplierReceivedQty";
            } else {
              dataField = "GoodsInDetailComment";
            }
            this.focusOnEditingCell(0, dataField);
          }
        }
      }
    },

    // Key Navigation On Data Grid
    onSupplierReceivedQtyKeyDown(e, cell) {
      if (e.event) {
        let gridInstance = this.getDataGridInstance();
        if (e.event.key == "Tab" && e.event.shiftKey == true) {
          if (cell.data.rowIndex == 0) {
            gridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            setTimeout(() => {
              if (
                this.headerToolBarForm &&
                this.headerToolBarForm.getEditor("DateReceived")
              )
                this.headerToolBarForm
                  .getEditor("DateReceived")
                  .instance()
                  .focus();
            }, 10);
          } else {
            var gridDS = gridInstance.getDataSource();
            var loadOptions = gridDS.loadOptions();
            gridDS
              .store()
              .load(loadOptions)
              .done((loadedItems) => {
                let index = loadedItems.findIndex(
                  (x) => x.LineNo == cell.data.data.LineNo,
                );
                if (index != -1) {
                  let prevLine = loadedItems[index - 1];
                  if (prevLine.IsBatchProduct != "No") {
                    e.event.preventDefault();
                    gridInstance.option("focusedRowIndex", -1);
                    gridInstance.expandRow(prevLine.LineNo).then(() => {
                      // Prev Items is Batch
                      this.moveIntoLastFiedlInBatch(e, prevLine);
                    });
                  }
                }
              });
          }
        }
      }
    },
    onGoodsInDetailCommentKeyDown(e, cellInfo) {
      if (e.event) {
        let gridInstance = this.getDataGridInstance();
        let data = cellInfo.data.data;
        if (e.event.key == "Tab" && e.event.shiftKey == false) {
          if (data.IsBatchProduct != "No") {
            gridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            gridInstance.expandRow(cellInfo.data.key).then(() => {
              setTimeout(() => {
                let batchGridInstance = this.getMasterDataInstance(data.LineNo);
                let items = batchGridInstance.getDataSource().items();

                if (batchGridInstance)
                  this.BatchGridInstance = batchGridInstance;

                if (items.length > 0) {
                  let dataField = undefined;
                  if (items[0].IsBatchProduct == "Manual") {
                    dataField = "BatchNo";
                  } else {
                    dataField = "SupplierBatchNo";
                  }
                  if (batchGridInstance) {
                    let cellElement = batchGridInstance.getCellElement(
                      0,
                      dataField,
                    );
                    let rowIndex = batchGridInstance.getRowIndexByKey(
                      items[0].LineNo,
                    );
                    if (cellElement) {
                      batchGridInstance.focus(cellElement);
                      setTimeout(() => {
                        batchGridInstance.editCell(rowIndex, dataField);
                      }, 1);
                    }
                  }
                }
              }, 10);
            });
          } else {
            let rowIndex = gridInstance.getRowIndexByKey(data.LineNo);
            let nextRowKey = gridInstance.getKeyByRowIndex(rowIndex + 1);

            if (nextRowKey) {
              gridInstance.byKey(nextRowKey).then((res) => {
                if (res.IsBatchProduct != "No") {
                  e.event.preventDefault();
                  this.focusOnEditingCell(rowIndex + 1, "GoodsInDetailComment");
                }
              });
            }
          }
        } else if (e.event.key == "Tab" && e.event.shiftKey == true) {
          let prevRowKey = gridInstance.getKeyByRowIndex(
            cellInfo.data.rowIndex - 1,
          );

          if (data.IsBatchProduct != "No") {
            gridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            if (prevRowKey != -1) {
              gridInstance.byKey(prevRowKey).then((prevItem) => {
                if (prevItem.IsBatchProduct == "No") {
                  // Prev Items is not Batch
                  let rowIndex = gridInstance.getRowIndexByKey(prevItem.LineNo);
                  this.focusOnEditingCell(rowIndex, "GoodsInDetailComment");
                } else {
                  // Prev Items is Batch
                  this.moveIntoLastFiedlInBatch(e, prevItem);
                }
              });
            } else {
              setTimeout(() => {
                if (
                  this.headerToolBarForm &&
                  this.headerToolBarForm.getEditor("DateReceived")
                )
                  this.headerToolBarForm
                    .getEditor("DateReceived")
                    .instance()
                    .focus();
              }, 10);
            }
          }
        }
      }
    },
    moveIntoLastFiedlInBatch(e, prevItem) {
      let gridInstance = this.getDataGridInstance();
      gridInstance.expandRow(prevItem.LineNo).then(() => {
        let batchGridInstance = this.getMasterDataInstance(prevItem.LineNo);
        if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
        let items = batchGridInstance.getDataSource().items();

        if (items.length > 0) {
          e.event.preventDefault();
          let item = items[items.length - 1];

          let visibleUseBy = true;
          let visibleBestBefore = true;

          if (item.UseByShelfLife == -1 && item.BestBeforeShelfLife == -1) {
            visibleUseBy = false;
            visibleBestBefore = false;
          } else if (
            item.UseByShelfLife > -1 &&
            item.BestBeforeShelfLife > -1
          ) {
            visibleBestBefore = false;
          } else if (item.UseByShelfLife > -1) {
            visibleBestBefore = false;
          } else if (item.BestBeforeShelfLife > -1) {
            visibleUseBy = false;
          }

          let dataField = "SupplierReceivedQty";

          if (item.FromBatchDB) dataField = "SupplierBatchReceivedQty";
          else if (!item.ManufacturedDate) dataField = "ManufacturedDate";
          else if (visibleUseBy) dataField = "UseByDate";
          else if (visibleBestBefore) dataField = "BestBeforeDate";

          if (batchGridInstance) {
            let cellElement = batchGridInstance.getCellElement(
              items.length - 1,
              dataField,
            );
            if (cellElement) {
              let rowIndex = batchGridInstance.getRowIndexByKey(
                items[items.length - 1].LineNo,
              );
              batchGridInstance.focus(cellElement);
              setTimeout(() => {
                batchGridInstance.editCell(rowIndex, dataField);
              }, 1);
            }
          }
        }
      });
    },
    onFocusedCellChanged() {
      if (this.BatchGridInstance)
        this.BatchGridInstance.option("focusedRowIndex", -1);
    },

    // Key Nav on Batch Grid
    onBatchNoKeyDown(e, cellInfo) {
      if (e.event) {
        let batchGridInstance = cellInfo.component;
        if (e.event.key == "Tab" && e.event.shiftKey == true) {
          if (cellInfo.data.rowIndex == 0) {
            if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
            batchGridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            let data = cellInfo.data.data;
            let instance = this.getDataGridInstance();
            let parentRowIndex = instance.getRowIndexByKey(data.ParentLineNo);

            this.focusOnEditingCell(parentRowIndex, "GoodsInDetailComment");
          }
        } else if (e.event.key == "Tab" && e.event.shiftKey == false) {
          this.batchNoTabKey = true;

          if (cellInfo.data.data.FromBatchDB) {
            e.event.preventDefault();
            let item = cellInfo.data.data;
            if (batchGridInstance) {
              let cellElement = batchGridInstance.getCellElement(
                0,
                "SupplierBatchReceivedQty",
              );
              let rowIndex = batchGridInstance.getRowIndexByKey(item.LineNo);
              if (cellElement) {
                batchGridInstance.focus(cellElement);
                setTimeout(() => {
                  batchGridInstance.editCell(
                    rowIndex,
                    "SupplierBatchReceivedQty",
                  );
                }, 1);
              }
            }
          }
        }
      }
    },
    onSupplierBatchNoKeyDown(e, cellInfo) {
      if (e.event) {
        if (e.event.key == "Tab" && e.event.shiftKey == true) {
          let data = cellInfo.data.data;
          if (data.BatchNo == "System Generated") {
            let batchGridInstance = cellInfo.component;
            if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
            batchGridInstance.option("focusedRowIndex", -1);
            e.event.preventDefault();
            let instance = this.getDataGridInstance();
            let parentRowIndex = instance.getRowIndexByKey(data.ParentLineNo);

            this.focusOnEditingCell(parentRowIndex, "GoodsInDetailComment");
          }
        }
      }
    },
    onSupplierBatchReceivedQtyDown(e, cellInfo) {
      if (e.event) {
        let batchGridInstance = cellInfo.component;
        let item = cellInfo.data.data;
        if (e.event.key == "Tab" && e.event.shiftKey == false) {
          if (cellInfo.data.data.FromBatchDB) {
            e.event.preventDefault();
            this.onUseByDateKeyDown(e, cellInfo);
          }
        } else if (e.event.key == "Tab" && e.event.shiftKey == true) {
          if (cellInfo.data.data.FromBatchDB) {
            e.event.preventDefault();
            if (batchGridInstance) {
              let cellElement = batchGridInstance.getCellElement(0, "BatchNo");
              let rowIndex = batchGridInstance.getRowIndexByKey(item.LineNo);
              if (cellElement) {
                batchGridInstance.focus(cellElement);
                setTimeout(() => {
                  batchGridInstance.editCell(rowIndex, "BatchNo");
                }, 1);
              }
            }
          }
        }
      }
    },
    onManufacturedDateKeyDown(e, cellInfo) {
      setTimeout(() => {
        if (e.event) {
          let data = cellInfo.data.data;
          let gridInstance = this.getDataGridInstance();
          let batchGridInstance = cellInfo.component;
          if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
          let items = batchGridInstance.getDataSource().items();

          let visibleUseBy = true;
          let visibleBestBefore = true;

          if (data.UseByShelfLife == -1 && data.BestBeforeShelfLife == -1) {
            visibleUseBy = false;
            visibleBestBefore = false;
          } else if (
            data.UseByShelfLife > -1 &&
            data.BestBeforeShelfLife > -1
          ) {
            visibleBestBefore = false;
          } else if (data.UseByShelfLife > -1) {
            visibleBestBefore = false;
          } else if (data.BestBeforeShelfLife > -1) {
            visibleUseBy = false;
          }

          if (e.event.key == "Tab" && e.event.shiftKey == false) {
            let rowIndex = cellInfo.data.rowIndex;
            if (
              rowIndex == items.length - 1 &&
              (!data.ManufacturedDate ||
                (data.ManufacturedDate && !visibleUseBy && !visibleBestBefore))
            ) {
              e.event.preventDefault();
              batchGridInstance.option("focusedRowIndex", -1);

              var gridDS = gridInstance.getDataSource();
              var loadOptions = gridDS.loadOptions();
              gridDS
                .store()
                .load(loadOptions)
                .done((loadedItems) => {
                  let parentRowIndex = loadedItems.findIndex(
                    (x) => x.LineNo == data.ParentLineNo,
                  );
                  let nextRowIndex = parentRowIndex + 1;

                  let nextRow = loadedItems[nextRowIndex];

                  if (nextRow) {
                    let dataField = undefined;
                    if (nextRow.IsBatchProduct === "No") {
                      dataField = "SupplierReceivedQty";
                    } else {
                      dataField = "GoodsInDetailComment";
                    }
                    let rowIndex = gridInstance.getRowIndexByKey(
                      nextRow.LineNo,
                    );
                    this.focusOnEditingCell(rowIndex, dataField);
                  }
                });
            } else if (
              data.ManufacturedDate &&
              (visibleUseBy || visibleBestBefore)
            ) {
              let rowIndex = batchGridInstance.getRowIndexByKey(data.LineNo);
              if (rowIndex != -1) {
                let dataField = undefined;

                if (visibleUseBy) dataField = "UseByDate";
                else if (visibleBestBefore) dataField = "BestBeforeDate";

                if (dataField) {
                  e.event.preventDefault();
                  setTimeout(() => {
                    if (batchGridInstance) {
                      let cellElement = batchGridInstance.getCellElement(
                        rowIndex,
                        dataField,
                      );
                      if (cellElement) {
                        batchGridInstance.focus(cellElement);
                        setTimeout(() => {
                          batchGridInstance.editCell(rowIndex, dataField);
                        }, 1);
                      }
                    }
                  }, 10);
                }
              }
            }
          }
        }
      }, 1);
    },
    onUseByDateKeyDown(e, cellInfo) {
      if (e.event) {
        let data = cellInfo.data.data;
        let gridInstance = this.getDataGridInstance();
        let batchGridInstance = cellInfo.component;
        if (batchGridInstance) this.BatchGridInstance = batchGridInstance;
        let items = batchGridInstance.getDataSource().items();

        if (e.event.key == "Tab" && e.event.shiftKey == false) {
          let rowIndex = cellInfo.data.rowIndex;
          if (rowIndex == items.length - 1) {
            e.event.preventDefault();
            batchGridInstance.option("focusedRowIndex", -1);

            var gridDS = gridInstance.getDataSource();
            var loadOptions = gridDS.loadOptions();
            gridDS
              .store()
              .load(loadOptions)
              .done((loadedItems) => {
                let parentRowIndex = loadedItems.findIndex(
                  (x) => x.LineNo == data.ParentLineNo,
                );
                let nextRowIndex = parentRowIndex + 1;

                let nextRow = loadedItems[nextRowIndex];

                setTimeout(() => {
                  if (nextRow) {
                    let dataField = undefined;
                    if (nextRow.IsBatchProduct === "No") {
                      dataField = "SupplierReceivedQty";
                    } else {
                      dataField = "GoodsInDetailComment";
                    }
                    let rowIndex = gridInstance.getRowIndexByKey(
                      nextRow.LineNo,
                    );
                    this.focusOnEditingCell(rowIndex, dataField);
                  }
                }, 10);
              });
          }
        }
      }
    },

    // Footer
    async setFooterItems(gridItems) {
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.dataModel, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          if (x.CheckInclude || this.baseView.mode == "review")
            result += parseFloat(x[dataField]).roundNumber(2);
          else if (x.CheckInclude == undefined)
            result += parseFloat(x[dataField]).roundNumber(2);
        });

      return result;
    },
    onLabelPrintingClose: function () {
      this.labelPrintingVisible = false;
      this.$router.push({ path: "/goods-in" });
    },
  },
  watch: {
    localDataSource: {
      handler(val) {
        if (val) this.setUpdateHeaderComplete();

        this.setFooterItems(val._array);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.goods-in {
  .edit-cell-datebox-field-border .dx-datebox::after {
    content: "";
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  }

  .gc-require-validation::after,
  .gc-require-validation .dx-texteditor::after {
    content: "";
    border-bottom: 2px solid rgb(255 0 0) !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: top !important;
  }

  .alert-row {
    background-color: #ffcccc; /* Light red background */
    color: #a94442; /* Dark red text */
  }

  .exp-date-template {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .dx-icon {
      font-size: 15px;
    }
  }
}
</style>
